import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { toast } from 'sonner';
import {
  User, Package, MapPin, Heart, LogOut, Edit2, Plus, Trash2,
  Clock, CheckCircle, Truck, XCircle, ChevronRight, Phone, Mail,
  Eye, AlertCircle, Calendar, Loader2, LayoutDashboard, Wrench,
  ShoppingBag, ArrowRight, ExternalLink, Search, X
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ORDER_STATUS = {
  pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  confirmed: { label: 'Confirmed', color: 'bg-blue-100 text-blue-800', icon: CheckCircle },
  processing: { label: 'Processing', color: 'bg-indigo-100 text-indigo-800', icon: Clock },
  shipped: { label: 'Shipped', color: 'bg-purple-100 text-purple-800', icon: Truck },
  out_for_delivery: { label: 'Out for Delivery', color: 'bg-orange-100 text-orange-800', icon: Truck },
  delivered: { label: 'Delivered', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-800', icon: XCircle }
};

const BOOKING_STATUS = {
  pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-800' },
  confirmed: { label: 'Confirmed', color: 'bg-blue-100 text-blue-800' },
  assigned: { label: 'Assigned', color: 'bg-purple-100 text-purple-800' },
  in_progress: { label: 'In Progress', color: 'bg-orange-100 text-orange-800' },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-800' },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-800' }
};

function AccountPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, isAuthenticated, loading: authLoading, logout, updateProfile } = useAuth();

  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'dashboard');
  const [orders, setOrders] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({ name: '', phone: '' });
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [addressForm, setAddressForm] = useState({
    full_name: '', phone: '', address: '', city: '', state: 'Rajasthan',
    pincode: '', landmark: '', address_type: 'home', is_default: false
  });

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login', { state: { from: '/account' } });
    }
  }, [isAuthenticated, authLoading, navigate]);

  useEffect(() => {
    if (user) setProfileForm({ name: user.name || '', phone: user.phone || '' });
  }, [user]);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab) setActiveTab(tab);
  }, [searchParams]);

  const switchTab = (tab) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  const fetchData = useCallback(async () => {
    if (!isAuthenticated || !user) return;
    setLoadingData(true);
    try {
      if (activeTab === 'dashboard' || activeTab === 'orders') {
        const res = await fetch(`${BACKEND_URL}/api/user/orders?email=${encodeURIComponent(user.email)}`, { credentials: 'include' });
        if (res.ok) setOrders(await res.json());
      }
      if (activeTab === 'dashboard' || activeTab === 'bookings') {
        const res = await fetch(`${BACKEND_URL}/api/user/bookings?email=${encodeURIComponent(user.email)}`);
        if (res.ok) setBookings(await res.json());
      }
      if (activeTab === 'addresses') {
        const res = await fetch(`${BACKEND_URL}/api/user/addresses`, { credentials: 'include' });
        if (res.ok) setAddresses(await res.json());
        else {
          const res2 = await fetch(`${BACKEND_URL}/api/user/addresses?user_id=${user.user_id}`);
          if (res2.ok) setAddresses(await res2.json());
        }
      }
      if (activeTab === 'wishlist') {
        const res = await fetch(`${BACKEND_URL}/api/user/wishlist`, { credentials: 'include' });
        if (res.ok) setWishlist(await res.json());
      }
    } catch (e) { console.error('Fetch error:', e); }
    finally { setLoadingData(false); }
  }, [activeTab, isAuthenticated, user]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '-';
  const formatDateTime = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';

  const canCancelOrder = (order) => {
    if (['shipped', 'delivered', 'cancelled', 'out_for_delivery'].includes(order.status)) return false;
    if (!order.created_at) return false;
    const hours = (Date.now() - new Date(order.created_at).getTime()) / (1000 * 60 * 60);
    return hours <= 24;
  };

  const handleCancelOrder = async () => {
    if (!showCancelModal || !cancelReason.trim()) { toast.error('Please provide a reason'); return; }
    setCancelling(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/orders/${showCancelModal.id}/cancel`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: cancelReason })
      });
      if (res.ok) { toast.success('Order cancelled'); setShowCancelModal(null); setCancelReason(''); fetchData(); }
      else { const err = await res.json(); toast.error(err.detail || 'Failed'); }
    } catch { toast.error('Failed to cancel'); }
    finally { setCancelling(false); }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    try { await updateProfile(profileForm); setEditingProfile(false); toast.success('Profile updated'); }
    catch { toast.error('Failed to update profile'); }
  };

  const handleAddressSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingAddress
        ? `${BACKEND_URL}/api/user/addresses/${editingAddress.address_id}`
        : `${BACKEND_URL}/api/user/addresses?user_id=${user.user_id}`;
      const method = editingAddress ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method, credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(addressForm)
      });
      if (res.ok) {
        toast.success(editingAddress ? 'Address updated' : 'Address added');
        resetAddressForm(); fetchData();
      } else { toast.error('Failed to save address'); }
    } catch { toast.error('Failed to save address'); }
  };

  const handleDeleteAddress = async (addressId) => {
    if (!window.confirm('Delete this address?')) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/user/addresses/${addressId}`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) { toast.success('Address deleted'); fetchData(); }
    } catch { toast.error('Failed to delete'); }
  };

  const resetAddressForm = () => {
    setShowAddressForm(false); setEditingAddress(null);
    setAddressForm({ full_name: '', phone: '', address: '', city: '', state: 'Rajasthan', pincode: '', landmark: '', address_type: 'home', is_default: false });
  };

  const startEditAddress = (addr) => {
    setEditingAddress(addr);
    setAddressForm({
      full_name: addr.full_name || '', phone: addr.phone || '', address: addr.address || '',
      city: addr.city || '', state: addr.state || 'Rajasthan', pincode: addr.pincode || '',
      landmark: addr.landmark || '', address_type: addr.address_type || 'home', is_default: addr.is_default || false
    });
    setShowAddressForm(true);
  };

  const removeFromWishlist = async (productId) => {
    try {
      await fetch(`${BACKEND_URL}/api/user/wishlist/${productId}`, { method: 'DELETE', credentials: 'include' });
      setWishlist(prev => prev.filter(p => p.id !== productId));
      toast.success('Removed from wishlist');
    } catch { toast.error('Failed'); }
  };

  if (authLoading) {
    return <div className="min-h-screen bg-gray-50"><Header /><div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div><Footer /></div>;
  }

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'orders', label: 'My Orders', icon: Package, count: orders.length },
    { id: 'bookings', label: 'My Bookings', icon: Wrench, count: bookings.length },
    { id: 'addresses', label: 'Addresses', icon: MapPin, count: addresses.length },
    { id: 'wishlist', label: 'Wishlist', icon: Heart },
    { id: 'profile', label: 'Profile', icon: User }
  ];

  const activeOrders = orders.filter(o => !['delivered', 'cancelled'].includes(o.status));
  const completedOrders = orders.filter(o => o.status === 'delivered');

  return (
    <div className="min-h-screen bg-gray-50" data-testid="account-page">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">My Account</h1>
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Sidebar */}
          <div className="lg:w-64 shrink-0">
            <div className="bg-white rounded-xl shadow-sm p-4">
              <div className="flex items-center gap-3 pb-4 border-b mb-4">
                {user?.picture ? (
                  <img src={user.picture} alt={user.name} className="w-12 h-12 rounded-full" />
                ) : (
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center"><User className="w-6 h-6 text-blue-600" /></div>
                )}
                <div className="min-w-0">
                  <p className="font-medium text-gray-900 truncate">{user?.name || 'User'}</p>
                  <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                </div>
              </div>
              <nav className="space-y-1" data-testid="account-nav">
                {tabs.map(tab => (
                  <button key={tab.id} onClick={() => switchTab(tab.id)} data-testid={`tab-${tab.id}`}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left text-sm transition-colors ${
                      activeTab === tab.id ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-600 hover:bg-gray-50'
                    }`}>
                    <tab.icon size={18} />
                    <span className="flex-1">{tab.label}</span>
                    {tab.count > 0 && <span className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-full">{tab.count}</span>}
                  </button>
                ))}
                <button onClick={() => { logout(); navigate('/'); }} data-testid="logout-btn"
                  className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left text-sm text-red-600 hover:bg-red-50">
                  <LogOut size={18} /> Logout
                </button>
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            <div className="bg-white rounded-xl shadow-sm p-6">

              {/* ===== DASHBOARD TAB ===== */}
              {activeTab === 'dashboard' && (
                <div data-testid="dashboard-tab">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Welcome back, {user?.name?.split(' ')[0] || 'there'}!</h2>

                  {/* Quick Stats */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    {[
                      { label: 'Active Orders', value: activeOrders.length, icon: Package, color: 'text-blue-600 bg-blue-50', click: () => switchTab('orders') },
                      { label: 'Total Orders', value: orders.length, icon: ShoppingBag, color: 'text-green-600 bg-green-50', click: () => switchTab('orders') },
                      { label: 'Bookings', value: bookings.length, icon: Wrench, color: 'text-purple-600 bg-purple-50', click: () => switchTab('bookings') },
                      { label: 'Addresses', value: addresses.length, icon: MapPin, color: 'text-orange-600 bg-orange-50', click: () => switchTab('addresses') }
                    ].map(s => (
                      <button key={s.label} onClick={s.click} className="border rounded-xl p-4 text-left hover:shadow-md transition-shadow">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-2 ${s.color}`}><s.icon size={20} /></div>
                        <p className="text-2xl font-bold text-gray-900">{s.value}</p>
                        <p className="text-xs text-gray-500">{s.label}</p>
                      </button>
                    ))}
                  </div>

                  {/* Active Orders */}
                  {activeOrders.length > 0 && (
                    <div className="mb-6">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-medium text-gray-900">Active Orders</h3>
                        <button onClick={() => switchTab('orders')} className="text-sm text-blue-600 hover:underline">View All</button>
                      </div>
                      <div className="space-y-3">
                        {activeOrders.slice(0, 3).map(order => {
                          const s = ORDER_STATUS[order.status] || ORDER_STATUS.pending;
                          return (
                            <div key={order.id} className="border rounded-lg p-3 flex items-center gap-4 hover:bg-gray-50 cursor-pointer"
                              onClick={() => setSelectedOrder(order)} data-testid={`active-order-${order.id}`}>
                              <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center"><Package size={20} className="text-blue-600" /></div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-sm text-gray-900 truncate">#{order.order_number || order.id}</p>
                                <p className="text-xs text-gray-500">{formatDate(order.created_at)} - Rs.{order.total?.toLocaleString()}</p>
                              </div>
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${s.color}`}>{s.label}</span>
                              <ChevronRight size={16} className="text-gray-400" />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Recent Bookings */}
                  {bookings.length > 0 && (
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-medium text-gray-900">Recent Bookings</h3>
                        <button onClick={() => switchTab('bookings')} className="text-sm text-blue-600 hover:underline">View All</button>
                      </div>
                      <div className="space-y-3">
                        {bookings.slice(0, 3).map(booking => {
                          const s = BOOKING_STATUS[booking.status] || BOOKING_STATUS.pending;
                          return (
                            <div key={booking.id} className="border rounded-lg p-3 flex items-center gap-4 hover:bg-gray-50 cursor-pointer"
                              onClick={() => setSelectedBooking(booking)}>
                              <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center"><Wrench size={20} className="text-purple-600" /></div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-sm text-gray-900 truncate">{booking.serviceType || 'Service'}</p>
                                <p className="text-xs text-gray-500">{formatDate(booking.created_at)}</p>
                              </div>
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${s.color}`}>{s.label}</span>
                              <ChevronRight size={16} className="text-gray-400" />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {orders.length === 0 && bookings.length === 0 && (
                    <div className="text-center py-12">
                      <ShoppingBag className="w-16 h-16 text-gray-200 mx-auto mb-4" />
                      <h3 className="font-medium text-gray-900 mb-2">No activity yet</h3>
                      <p className="text-gray-500 text-sm mb-4">Start shopping or book a service to see activity here</p>
                      <Link to="/products" className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm">
                        Browse Products <ArrowRight size={16} />
                      </Link>
                    </div>
                  )}
                </div>
              )}

              {/* ===== ORDERS TAB ===== */}
              {activeTab === 'orders' && (
                <div data-testid="orders-tab">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">My Orders</h2>
                  {loadingData ? <LoadingSpinner /> : orders.length === 0 ? (
                    <EmptyState icon={Package} title="No orders yet" desc="Start shopping to see your orders here" cta="Browse Products" link="/products" />
                  ) : (
                    <div className="space-y-4">
                      {orders.map(order => {
                        const s = ORDER_STATUS[order.status] || ORDER_STATUS.pending;
                        const Icon = s.icon;
                        return (
                          <div key={order.id} className="border rounded-xl p-4 hover:shadow-md transition-shadow" data-testid={`order-card-${order.id}`}>
                            <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                              <div>
                                <p className="font-medium text-gray-900">Order #{order.order_number || order.id}</p>
                                <p className="text-sm text-gray-500">{formatDateTime(order.created_at)}</p>
                              </div>
                              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${s.color}`}>
                                <Icon size={14} />{s.label}
                              </span>
                            </div>
                            {/* Items preview */}
                            <div className="flex flex-wrap gap-2 mb-3">
                              {order.items?.slice(0, 3).map((item, i) => (
                                <div key={i} className="flex items-center gap-2 bg-gray-50 rounded-lg p-2 text-sm">
                                  {item.image && <img src={item.image} alt="" className="w-8 h-8 rounded object-contain" />}
                                  <span className="text-gray-700 line-clamp-1 max-w-[120px]">{item.name}</span>
                                </div>
                              ))}
                              {order.items?.length > 3 && <span className="text-xs text-gray-400 self-center">+{order.items.length - 3} more</span>}
                            </div>
                            <div className="flex flex-wrap items-center justify-between gap-3 pt-3 border-t">
                              <p className="font-semibold text-gray-900">Rs.{order.total?.toLocaleString()}</p>
                              <div className="flex gap-2">
                                <Link to={`/track/order/${order.id}`} className="flex items-center gap-1 px-3 py-1.5 text-xs text-blue-600 hover:bg-blue-50 rounded-lg border border-blue-200" data-testid={`track-order-${order.id}`}>
                                  <Truck size={14} /> Track
                                </Link>
                                <button onClick={() => setSelectedOrder(order)} className="flex items-center gap-1 px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50 rounded-lg border" data-testid={`view-order-${order.id}`}>
                                  <Eye size={14} /> Details
                                </button>
                                {canCancelOrder(order) && (
                                  <button onClick={() => setShowCancelModal(order)} className="flex items-center gap-1 px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 rounded-lg border border-red-200" data-testid={`cancel-order-${order.id}`}>
                                    <XCircle size={14} /> Cancel
                                  </button>
                                )}
                              </div>
                            </div>
                            {order.tracking_number && (
                              <div className="mt-3 p-2 bg-purple-50 rounded-lg text-xs text-purple-700">
                                <strong>Tracking:</strong> {order.tracking_number}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* ===== BOOKINGS TAB ===== */}
              {activeTab === 'bookings' && (
                <div data-testid="bookings-tab">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">My Service Bookings</h2>
                  {loadingData ? <LoadingSpinner /> : bookings.length === 0 ? (
                    <EmptyState icon={Wrench} title="No bookings yet" desc="Book a service for your RO system" cta="Book a Service" link="/services" />
                  ) : (
                    <div className="space-y-4">
                      {bookings.map(booking => {
                        const s = BOOKING_STATUS[booking.status] || BOOKING_STATUS.pending;
                        return (
                          <div key={booking.id} className="border rounded-xl p-4 hover:shadow-md transition-shadow" data-testid={`booking-card-${booking.id}`}>
                            <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                              <div>
                                <p className="font-medium text-gray-900">{booking.serviceType || 'Service'}</p>
                                <p className="text-xs text-gray-500">ID: {booking.id?.substring(0, 8)}... | {formatDate(booking.created_at)}</p>
                              </div>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${s.color}`}>{s.label}</span>
                            </div>
                            <div className="grid grid-cols-2 gap-3 text-sm mb-3">
                              <div><p className="text-gray-400 text-xs">Preferred Date</p><p className="text-gray-700">{booking.preferredDate || '-'}</p></div>
                              <div><p className="text-gray-400 text-xs">Preferred Time</p><p className="text-gray-700">{booking.preferredTime || '-'}</p></div>
                              {booking.assigned_technician && (
                                <div className="col-span-2"><p className="text-gray-400 text-xs">Assigned Technician</p><p className="text-purple-700 font-medium">{booking.assigned_technician}</p></div>
                              )}
                            </div>
                            <div className="flex gap-2 pt-3 border-t">
                              <Link to={`/track/booking/${booking.id}`} className="flex items-center gap-1 px-3 py-1.5 text-xs text-blue-600 hover:bg-blue-50 rounded-lg border border-blue-200" data-testid={`track-booking-${booking.id}`}>
                                <ExternalLink size={14} /> Track
                              </Link>
                              <button onClick={() => setSelectedBooking(booking)} className="flex items-center gap-1 px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50 rounded-lg border">
                                <Eye size={14} /> Details
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* ===== ADDRESSES TAB ===== */}
              {activeTab === 'addresses' && (
                <div data-testid="addresses-tab">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-gray-900">Saved Addresses</h2>
                    <button onClick={() => { resetAddressForm(); setShowAddressForm(true); }} data-testid="add-address-btn"
                      className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                      <Plus size={16} /> Add Address
                    </button>
                  </div>

                  {showAddressForm && (
                    <form onSubmit={handleAddressSubmit} className="border rounded-xl p-4 mb-4 bg-gray-50" data-testid="address-form">
                      <h3 className="font-medium text-gray-900 mb-3">{editingAddress ? 'Edit Address' : 'Add New Address'}</h3>
                      <div className="grid md:grid-cols-2 gap-3">
                        <input type="text" required placeholder="Full Name" value={addressForm.full_name} onChange={e => setAddressForm({...addressForm, full_name: e.target.value})} className="px-3 py-2 border rounded-lg text-sm" data-testid="addr-name" />
                        <input type="tel" required placeholder="Phone" value={addressForm.phone} onChange={e => setAddressForm({...addressForm, phone: e.target.value})} className="px-3 py-2 border rounded-lg text-sm" data-testid="addr-phone" />
                        <input type="text" required placeholder="Address" value={addressForm.address} onChange={e => setAddressForm({...addressForm, address: e.target.value})} className="md:col-span-2 px-3 py-2 border rounded-lg text-sm" data-testid="addr-address" />
                        <input type="text" required placeholder="City" value={addressForm.city} onChange={e => setAddressForm({...addressForm, city: e.target.value})} className="px-3 py-2 border rounded-lg text-sm" data-testid="addr-city" />
                        <input type="text" required placeholder="Pincode" value={addressForm.pincode} onChange={e => setAddressForm({...addressForm, pincode: e.target.value})} className="px-3 py-2 border rounded-lg text-sm" data-testid="addr-pincode" />
                        <input type="text" placeholder="Landmark (optional)" value={addressForm.landmark} onChange={e => setAddressForm({...addressForm, landmark: e.target.value})} className="px-3 py-2 border rounded-lg text-sm" />
                        <select value={addressForm.address_type} onChange={e => setAddressForm({...addressForm, address_type: e.target.value})} className="px-3 py-2 border rounded-lg text-sm">
                          <option value="home">Home</option><option value="work">Work</option><option value="other">Other</option>
                        </select>
                      </div>
                      <label className="flex items-center gap-2 mt-3 text-sm text-gray-600">
                        <input type="checkbox" checked={addressForm.is_default} onChange={e => setAddressForm({...addressForm, is_default: e.target.checked})} /> Set as default address
                      </label>
                      <div className="flex gap-2 mt-4">
                        <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700" data-testid="save-address-btn">
                          {editingAddress ? 'Update' : 'Save'} Address
                        </button>
                        <button type="button" onClick={resetAddressForm} className="px-4 py-2 border rounded-lg text-sm hover:bg-gray-50">Cancel</button>
                      </div>
                    </form>
                  )}

                  {loadingData ? <LoadingSpinner /> : addresses.length === 0 && !showAddressForm ? (
                    <EmptyState icon={MapPin} title="No addresses saved" desc="Add your delivery address" />
                  ) : (
                    <div className="grid md:grid-cols-2 gap-4">
                      {addresses.map(addr => (
                        <div key={addr.address_id} className="border rounded-xl p-4 relative" data-testid={`address-card-${addr.address_id}`}>
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded capitalize">{addr.address_type || 'home'}</span>
                              {addr.is_default && <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">Default</span>}
                            </div>
                            <div className="flex gap-1">
                              <button onClick={() => startEditAddress(addr)} className="p-1 hover:bg-gray-100 rounded" data-testid={`edit-addr-${addr.address_id}`}><Edit2 size={14} className="text-gray-400" /></button>
                              <button onClick={() => handleDeleteAddress(addr.address_id)} className="p-1 hover:bg-red-50 rounded" data-testid={`delete-addr-${addr.address_id}`}><Trash2 size={14} className="text-red-400" /></button>
                            </div>
                          </div>
                          <p className="font-medium text-gray-900 text-sm">{addr.full_name}</p>
                          <p className="text-gray-600 text-sm mt-1">{addr.address}</p>
                          <p className="text-gray-600 text-sm">{addr.city}, {addr.state} - {addr.pincode}</p>
                          {addr.landmark && <p className="text-gray-400 text-xs mt-1">Near: {addr.landmark}</p>}
                          <p className="text-gray-500 text-xs mt-1 flex items-center gap-1"><Phone size={12} />{addr.phone}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* ===== WISHLIST TAB ===== */}
              {activeTab === 'wishlist' && (
                <div data-testid="wishlist-tab">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">My Wishlist</h2>
                  {loadingData ? <LoadingSpinner /> : wishlist.length === 0 ? (
                    <EmptyState icon={Heart} title="Wishlist is empty" desc="Save products you love to buy later" cta="Browse Products" link="/products" />
                  ) : (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {wishlist.map(product => (
                        <div key={product.id} className="border rounded-xl overflow-hidden hover:shadow-md transition-shadow" data-testid={`wishlist-item-${product.id}`}>
                          <Link to={`/product/${product.slug || product.id}`}>
                            <img src={product.images?.[0] || product.image} alt={product.name} className="w-full h-40 object-contain bg-gray-50 p-2" />
                          </Link>
                          <div className="p-3">
                            <Link to={`/product/${product.slug || product.id}`} className="font-medium text-gray-900 text-sm line-clamp-2 hover:text-blue-600">{product.name}</Link>
                            <div className="flex items-center justify-between mt-2">
                              <p className="font-bold text-gray-900">Rs.{product.price?.toLocaleString()}</p>
                              <button onClick={() => removeFromWishlist(product.id)} className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg" data-testid={`remove-wish-${product.id}`}>
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* ===== PROFILE TAB ===== */}
              {activeTab === 'profile' && (
                <div data-testid="profile-tab">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-gray-900">Profile Information</h2>
                    {!editingProfile && <button onClick={() => setEditingProfile(true)} className="flex items-center gap-1 text-blue-600 hover:text-blue-700 text-sm" data-testid="edit-profile-btn"><Edit2 size={14} />Edit</button>}
                  </div>
                  {editingProfile ? (
                    <form onSubmit={handleProfileUpdate} className="space-y-4 max-w-md" data-testid="profile-form">
                      <div><label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                        <input type="text" value={profileForm.name} onChange={e => setProfileForm({...profileForm, name: e.target.value})} className="w-full px-3 py-2 border rounded-lg" data-testid="profile-name" /></div>
                      <div><label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                        <input type="tel" value={profileForm.phone} onChange={e => setProfileForm({...profileForm, phone: e.target.value})} className="w-full px-3 py-2 border rounded-lg" data-testid="profile-phone" /></div>
                      <div><label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                        <input type="email" disabled value={user?.email || ''} className="w-full px-3 py-2 border rounded-lg bg-gray-50 text-gray-500" />
                        <p className="text-xs text-gray-400 mt-1">Email cannot be changed</p></div>
                      <div className="flex gap-3">
                        <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700" data-testid="save-profile-btn">Save Changes</button>
                        <button type="button" onClick={() => setEditingProfile(false)} className="px-4 py-2 border rounded-lg text-sm hover:bg-gray-50">Cancel</button>
                      </div>
                    </form>
                  ) : (
                    <div className="space-y-4">
                      <div className="flex items-center gap-16">
                        {user?.picture && <img src={user.picture} alt={user.name} className="w-20 h-20 rounded-full border-2 border-gray-200" />}
                        <div className="space-y-3">
                          <InfoRow icon={User} label="Name" value={user?.name || '-'} />
                          <InfoRow icon={Mail} label="Email" value={user?.email || '-'} />
                          <InfoRow icon={Phone} label="Phone" value={user?.phone || 'Not provided'} />
                          <InfoRow icon={Calendar} label="Member Since" value={formatDate(user?.created_at)} />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Order Detail Modal */}
      {selectedOrder && <OrderDetailModal order={selectedOrder} onClose={() => setSelectedOrder(null)} canCancel={canCancelOrder(selectedOrder)} onCancel={() => { setSelectedOrder(null); setShowCancelModal(selectedOrder); }} formatDateTime={formatDateTime} />}

      {/* Booking Detail Modal */}
      {selectedBooking && <BookingDetailModal booking={selectedBooking} onClose={() => setSelectedBooking(null)} formatDateTime={formatDateTime} />}

      {/* Cancel Order Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="cancel-modal">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 text-red-600 mb-4"><AlertCircle size={24} /><h3 className="text-lg font-semibold">Cancel Order?</h3></div>
            <p className="text-gray-600 text-sm mb-4">Cancel order #{showCancelModal.order_number || showCancelModal.id}?</p>
            <textarea value={cancelReason} onChange={e => setCancelReason(e.target.value)} placeholder="Reason for cancellation..." rows={3}
              className="w-full px-3 py-2 border rounded-lg text-sm mb-4" data-testid="cancel-reason" />
            <div className="flex gap-3">
              <button onClick={() => { setShowCancelModal(null); setCancelReason(''); }} className="flex-1 px-4 py-2 border rounded-lg text-sm hover:bg-gray-50">Keep Order</button>
              <button onClick={handleCancelOrder} disabled={cancelling || !cancelReason.trim()}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 disabled:opacity-50" data-testid="confirm-cancel-btn">
                {cancelling ? 'Cancelling...' : 'Cancel Order'}
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}

// ===== Sub-components =====

function LoadingSpinner() {
  return <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;
}

function EmptyState({ icon: Icon, title, desc, cta, link }) {
  return (
    <div className="text-center py-12">
      <Icon className="w-16 h-16 text-gray-200 mx-auto mb-4" />
      <h3 className="font-medium text-gray-900 mb-2">{title}</h3>
      <p className="text-gray-500 text-sm mb-4">{desc}</p>
      {cta && link && <Link to={link} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm">{cta} <ArrowRight size={16} /></Link>}
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3">
      <Icon className="w-4 h-4 text-gray-400" />
      <div><p className="text-xs text-gray-400">{label}</p><p className="text-sm font-medium text-gray-900">{value}</p></div>
    </div>
  );
}

function OrderDetailModal({ order, onClose, canCancel, onCancel, formatDateTime }) {
  const s = ORDER_STATUS[order.status] || ORDER_STATUS.pending;
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto" data-testid="order-detail-modal">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto my-8">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between z-10">
          <div><h3 className="text-lg font-semibold text-gray-900">Order Details</h3><p className="text-sm text-gray-500">#{order.order_number || order.id}</p></div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-5">
          <div className="flex items-center gap-3">
            <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${s.color}`}><s.icon size={16} />{s.label}</span>
            <span className="text-sm text-gray-500">{formatDateTime(order.created_at)}</span>
          </div>
          <div>
            <h4 className="font-medium text-gray-900 mb-2">Items</h4>
            <div className="border rounded-lg divide-y">
              {order.items?.map((item, i) => (
                <div key={i} className="flex items-center gap-3 p-3">
                  {item.image && <img src={item.image} alt="" className="w-14 h-14 rounded-lg object-contain bg-gray-50" />}
                  <div className="flex-1"><p className="font-medium text-sm text-gray-900">{item.name}</p><p className="text-xs text-gray-500">Qty: {item.quantity}</p></div>
                  <p className="font-medium text-sm">Rs.{item.price?.toLocaleString()}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
            <div className="flex justify-between text-gray-500"><span>Subtotal</span><span>Rs.{order.subtotal?.toLocaleString()}</span></div>
            <div className="flex justify-between text-gray-500"><span>Delivery</span><span>{order.delivery_charge > 0 ? `Rs.${order.delivery_charge}` : 'Free'}</span></div>
            <div className="flex justify-between font-bold text-base pt-2 border-t"><span>Total</span><span>Rs.{order.total?.toLocaleString()}</span></div>
          </div>
          <div>
            <h4 className="font-medium text-gray-900 mb-1">Shipping Address</h4>
            <p className="text-gray-600 text-sm">{order.full_name || order.fullName}<br />{order.address}<br />{order.city}, {order.state} - {order.pincode}<br />Phone: {order.phone}</p>
          </div>
          {order.tracking_number && <div className="bg-purple-50 rounded-lg p-3"><p className="text-sm text-purple-700"><strong>Tracking:</strong> {order.tracking_number}</p></div>}
          <div className="flex gap-2 pt-2">
            <Link to={`/track/order/${order.id}`} className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700">
              <Truck size={16} /> Track Order
            </Link>
            {canCancel && <button onClick={onCancel} className="flex-1 px-4 py-2 border border-red-300 text-red-600 rounded-lg text-sm hover:bg-red-50">Cancel Order</button>}
          </div>
        </div>
      </div>
    </div>
  );
}

function BookingDetailModal({ booking, onClose, formatDateTime }) {
  const s = BOOKING_STATUS[booking.status] || BOOKING_STATUS.pending;
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto" data-testid="booking-detail-modal">
      <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto my-8">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between z-10">
          <div><h3 className="text-lg font-semibold text-gray-900">Booking Details</h3><p className="text-sm text-gray-500">ID: {booking.id?.substring(0, 12)}...</p></div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-5">
          <span className={`inline-flex px-3 py-1 rounded-full text-sm font-medium ${s.color}`}>{s.label}</span>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div><p className="text-gray-400 text-xs">Service Type</p><p className="font-medium text-gray-900 capitalize">{booking.serviceType?.replace(/-/g, ' ')}</p></div>
            <div><p className="text-gray-400 text-xs">Booked On</p><p className="font-medium text-gray-900">{formatDateTime(booking.created_at)}</p></div>
            <div><p className="text-gray-400 text-xs">Preferred Date</p><p className="font-medium text-gray-900">{booking.preferredDate || '-'}</p></div>
            <div><p className="text-gray-400 text-xs">Preferred Time</p><p className="font-medium text-gray-900">{booking.preferredTime || '-'}</p></div>
            {booking.assigned_technician && <div className="col-span-2"><p className="text-gray-400 text-xs">Assigned Technician</p><p className="font-medium text-purple-700">{booking.assigned_technician}</p></div>}
            {booking.scheduled_date && <div><p className="text-gray-400 text-xs">Scheduled Date</p><p className="font-medium text-gray-900">{booking.scheduled_date}</p></div>}
            {booking.scheduled_time && <div><p className="text-gray-400 text-xs">Scheduled Time</p><p className="font-medium text-gray-900">{booking.scheduled_time}</p></div>}
          </div>
          {(booking.message || booking.issue) && <div className="bg-yellow-50 rounded-lg p-3"><p className="text-sm text-yellow-800"><strong>Issue:</strong> {booking.message || booking.issue}</p></div>}
          <div>
            <h4 className="font-medium text-gray-900 mb-1">Contact</h4>
            <p className="text-gray-600 text-sm">{booking.name}<br />{booking.phone}<br />{booking.email}</p>
          </div>
          <Link to={`/track/booking/${booking.id}`} className="flex items-center justify-center gap-2 w-full px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700">
            <ExternalLink size={16} /> Track Booking
          </Link>
        </div>
      </div>
    </div>
  );
}

export default AccountPage;
